import { BuildingStorefrontIcon, GlobeAltIcon } from "@heroicons/react/24/solid";
import { useState } from "react";
import { memo, useCallback } from "react"
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import { VALIDATION_URL } from "../../../constants";
import { selectorTour } from "../../../features/tour/tourSlice";
import { ConfirmAction } from "../../ConfirmAction";
import { FormCardInput } from "../../FormCard"

/**
 * 
 * @param {{type?: string, site: Website, company_id: string, canAdd?: boolean, saveSite: (url: string) => void, deleteSite: (id: string) => void}} props 
 * @returns {JSX.Element}
 */
const FormDomain = ({ type = 'site', site, company_id, canAdd = true, saveSite, deleteSite }) => {
  const [openPreventRemove, setOpenPreventRemove] = useState(false)
  const loadingConcurrent = useSelector(state => state.concurrents.loading)
  const loadingWebsite = useSelector(state => state.websites.loading)
  const isTourLaunched = useSelector(selectorTour)

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm(
    {
      shouldFocusError: true,
      defaultValues: {
        title: site.title,
        site_url: site.site_url,
        website_id: site.id,
        company_id
      }
    }
  );

  const _saveSite = useCallback((newSite) => {
    if (site?.id)
      saveSite({ ...newSite, id: site.id })
    else saveSite(newSite)
  }, [site?.id])
  const _removeSite = useCallback((e) => {
    e.stopPropagation()
    _closeConfirmDialog()
    deleteSite(site.id)
  }, [site.id])

  const _preventRemove = useCallback((e) => {
    if (loadingConcurrent || loadingWebsite) return
    e.stopPropagation()
    if (site.id) setOpenPreventRemove(true)
    else deleteSite()
  }, [site, loadingConcurrent, loadingWebsite])

  const _closeConfirmDialog = useCallback(() => {
    setOpenPreventRemove(false)
  }, [])

  const isRunning = site.created_at ? Date.now() - new Date(site.created_at).getTime() <= 60 * 60 * 60 : false
  return <form onSubmit={handleSubmit(_saveSite)} className="flex gap-2 items-center justify-between bg-white p-3 rounded-2xl shadow">
    <ConfirmAction open={openPreventRemove} title={`Confirmation Suppression de votre ${type}`} question={`Êtes-vous sûr de vouloir supprimer votre ${type} ?`} description={`Sachez que la suppression entrainera la suppression de toutes les données liées à ce ${type}.`} onSuccess={_removeSite} onFailure={_closeConfirmDialog} />
    <FormCardInput
      disabled={isTourLaunched}
      placeholder='Ex: Azatys'
      label="Nom"
      name="title"
      rules={{ maxLength: 50, required: true }}
      errorType={errors?.title?.type}
      register={register}
      icon={<BuildingStorefrontIcon
        className="w-4 h-4 text-gray-500" />}
    />
    <FormCardInput
      disabled={isTourLaunched}
      placeholder='Ex: https://www.azatys.fr'
      type='url'
      label="URL"
      name="site_url"
      rules={{ required: true, pattern: VALIDATION_URL }}
      errorType={errors?.site_url?.type}
      register={register}
      icon={<GlobeAltIcon className="w-4 h-4 text-gray-500" />}
    />
    <div className="tooltip" data-tip={isRunning ? `Votre ${type} est en cours d'analyse, Revenez plus tard pour la mise à jour.` : 'Enregistrer'}>
      <button disabled={isTourLaunched || isRunning || loadingWebsite || loadingConcurrent || !canAdd} type='submit' className="rounded-full text-2xl h-10 w-10 aspect-square justify-center border-offers-teal flex items-center border-2 -mb-4 disabled:cursor-not-allowed text-offers-teal disabled:text-gray-500 disabled:bg-gray-100 disabled:border-gray-300">
        <SaveIcon className="w-4" />
      </button>
    </div>
    <div className="tooltip" data-tip='Supprimer'>
      <button type='button' disabled={isTourLaunched || loadingWebsite || loadingConcurrent} onClick={_preventRemove} className="rounded-full text-2xl h-10 w-10 aspect-square justify-center border-red-400 flex items-center border-2 -mb-4 disabled:cursor-not-allowed disabled:text-gray-500 disabled:bg-gray-100 disabled:border-gray-300">
        <DeleteIcon className="w-4 text-red-400" />
      </button>
    </div>
  </form>
}

export default memo(FormDomain)


export function SaveIcon(props) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" {...props} fill="currentColor"><path d="M5 21q-.825 0-1.413-.587Q3 19.825 3 19V5q0-.825.587-1.413Q4.175 3 5 3h11.175q.4 0 .763.15q.362.15.637.425l2.85 2.85q.275.275.425.637q.15.363.15.763V19q0 .825-.587 1.413Q19.825 21 19 21Zm7-3q1.25 0 2.125-.875T15 15q0-1.25-.875-2.125T12 12q-1.25 0-2.125.875T9 15q0 1.25.875 2.125T12 18Zm-5-8h7q.425 0 .713-.288Q15 9.425 15 9V7q0-.425-.287-.713Q14.425 6 14 6H7q-.425 0-.713.287Q6 6.575 6 7v2q0 .425.287.712Q6.575 10 7 10Z"></path></svg>
  )
}


export function DeleteIcon(props) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" {...props} fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v10zM18 4h-2.5l-.71-.71c-.18-.18-.44-.29-.7-.29H9.91c-.26 0-.52.11-.7.29L8.5 4H6c-.55 0-1 .45-1 1s.45 1 1 1h12c.55 0 1-.45 1-1s-.45-1-1-1z"></path></svg>
  )
}
